export const WORKSPACE_VIEWS = Object.freeze({
    timing: {
        title: "タイムチャート",
        help: "",
        tooltip: "複数軸の動作を縦に並べてガント風に編集します。動作バーをクリックすると詳細編集が表示されます。"
    },
    flow: {
        title: "フロー作成",
        help: "",
        tooltip: "開始、終了、動作、サブプロセス、条件、操作入力、信号操作、エラーノードを接続して制御の流れを組み立てます。"
    },
    settings: {
        title: "設定",
        help: "単位など、計算に関わる共通設定を管理します。"
    },
    "control-spec": {
        title: "制御仕様",
        help: "",
        tooltip: "信号一覧・アクチュエータの定義、I/O表やST（構造化テキスト）等の成果物出力、設計検証結果を管理します。"
    },
    skills: {
        title: "Skills",
        help: "ワークスペース内の標準仕様（Skills）一覧です。プロジェクトで使用する仕様を選択・適用できます。"
    }
});

export function normalizeWorkspaceView(viewId, fallback = "timing") {
    return WORKSPACE_VIEWS[viewId] ? viewId : fallback;
}

export function buildWorkspaceUiStorageValue(uiState = {}) {
    return {
        activeView: normalizeWorkspaceView(uiState.activeView),
        sidebarCollapsed: Boolean(uiState.sidebarCollapsed)
    };
}

export function applySavedWorkspaceUi(uiState = {}, saved = null) {
    if (!uiState || typeof uiState !== "object" || !saved || typeof saved !== "object") {
        return uiState;
    }
    uiState.activeView = normalizeWorkspaceView(saved.activeView);
    uiState.sidebarCollapsed = Boolean(saved.sidebarCollapsed);
    return uiState;
}

export function buildWorkspaceSessionScopeKey(session = null) {
    const userId = typeof session?.user?.id === "string" ? session.user.id : "";
    if (!userId) return "";
    const organizationId =
        typeof session?.organization?.id === "string" && session.organization.id
            ? session.organization.id
            : "personal";
    return `${userId}:${organizationId}`;
}

export function readWorkspaceScopedProjectId(raw = "", scopeKey = "") {
    if (typeof raw !== "string" || !raw) return null;
    if (!scopeKey) {
        return raw.trim().startsWith("{") ? null : raw;
    }
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
        return typeof parsed[scopeKey] === "string" && parsed[scopeKey]
            ? parsed[scopeKey]
            : null;
    } catch {
        return null;
    }
}

export function writeWorkspaceScopedProjectId(raw = "", scopeKey = "", projectId = null) {
    const nextId = typeof projectId === "string" && projectId ? projectId : null;
    if (!scopeKey) {
        return nextId || "";
    }
    let parsed = {};
    try {
        const candidate = JSON.parse(raw);
        if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
            parsed = Object.fromEntries(
                Object.entries(candidate).filter(([, value]) => typeof value === "string" && value)
            );
        }
    } catch {}
    if (nextId) parsed[scopeKey] = nextId;
    else delete parsed[scopeKey];
    return Object.keys(parsed).length > 0 ? JSON.stringify(parsed) : "";
}

export function handleWorkspaceScopeChange(deps = {}) {
    const {
        session = null,
        getPreviousScopeKey = () => undefined,
        setPreviousScopeKey = () => {},
        clearAutoSaveTimer = () => {},
        setCloudProjectId = () => {},
        rememberLastCloudProjectId = () => {},
        updateCloudSaveStatus = () => {},
        refs = {},
        clearAccountSettings = () => {},
        clearSkillUi = () => {},
        reloadSkills = () => {}
    } = deps;

    const nextScopeKey = buildWorkspaceSessionScopeKey(session);
    const previousScopeKey = getPreviousScopeKey();
    setPreviousScopeKey(nextScopeKey);

    if (previousScopeKey === undefined || previousScopeKey === nextScopeKey) {
        return false;
    }

    clearAutoSaveTimer();
    setCloudProjectId(null);
    rememberLastCloudProjectId(null, previousScopeKey);
    updateCloudSaveStatus("");
    clearAccountSettings();
    clearSkillUi();

    if (refs.cloudProjectSelect) {
        refs.cloudProjectSelect.innerHTML = "";
        refs.cloudProjectSelect.style.display = "none";
        refs.cloudProjectSelect.value = "";
    }
    if (refs.projectListPanel) {
        refs.projectListPanel.innerHTML = "";
        refs.projectListPanel.style.display = "none";
    }
    refs.btnProjectList?.setAttribute("aria-expanded", "false");

    if (session?.user) {
        reloadSkills();
    }
    return true;
}

export function applyHeaderActionsState(doc = document, options = {}) {
    const {
        activeView = "timing",
        assistantOpen = true
    } = options;
    const header = doc.querySelector(".app-header");
    if (header) header.classList.toggle("assistant-collapsed", !assistantOpen);
    const titleBar = doc.querySelector(".project-title-bar");
    if (titleBar) titleBar.classList.toggle("assistant-collapsed", !assistantOpen);
    let hasVisibleGroup = false;
    doc.querySelectorAll("[data-header-actions]").forEach((group) => {
        const allowed = (group.getAttribute("data-header-actions") || "").split(/\s+/).filter(Boolean);
        const visible = allowed.includes(activeView);
        group.classList.toggle("is-hidden", !visible);
        if (visible) hasVisibleGroup = true;
    });
    if (header) header.classList.toggle("is-hidden", !hasVisibleGroup);
}

export function applyActiveViewDom(doc = document, viewId = "timing", meta = WORKSPACE_VIEWS.timing) {
    doc.querySelectorAll("[data-view-panel]").forEach((panel) => {
        panel.classList.toggle("is-active", panel.dataset.viewPanel === viewId);
    });
    doc.querySelectorAll(".sidebar-link[data-view]").forEach((button) => {
        const active = button.dataset.view === viewId;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-current", active ? "page" : "false");
    });

    const title = doc.getElementById("activeViewTitle");
    const help = doc.getElementById("activeViewHelp");
    if (title) {
        title.textContent = meta.title;
        title.title = meta.tooltip || meta.help || "";
    }
    if (help) {
        help.textContent = meta.help || "";
        help.style.display = meta.help ? "" : "none";
    }
}

export function applySidebarCollapsedDom(doc = document, collapsed = false) {
    const isCollapsed = Boolean(collapsed);
    const shell = doc.getElementById("appShell");
    const toggle = doc.getElementById("btnSidebarToggle");
    if (shell) shell.classList.toggle("sidebar-collapsed", isCollapsed);
    if (toggle) {
        const label = isCollapsed ? "メニューを展開" : "メニューを折りたたむ";
        toggle.innerHTML = '<span class="sidebar-toggle-icon" aria-hidden="true"><svg class="sidebar-toggle-icon-open" viewBox="0 0 20 20" focusable="false"><rect x="3" y="4" width="14" height="12" rx="2"></rect><path d="M8 4v12"></path></svg><svg class="sidebar-toggle-icon-closed" viewBox="0 0 20 20" focusable="false"><rect x="3" y="4" width="14" height="12" rx="2"></rect><path d="M12 4v12"></path></svg></span>';
        toggle.title = label;
        toggle.setAttribute("aria-label", label);
        toggle.setAttribute("aria-expanded", String(!isCollapsed));
        toggle.setAttribute("data-sidebar-collapsed", String(isCollapsed));
    }
}
